import { useMemo, useState } from 'react'

import { OfReleaseFormsService } from '@api'
import { useUnit } from 'effector-react'
import debounce from 'lodash/debounce'
import { useInfiniteQuery } from 'react-query'

import { $creatorId } from '@/models/creators.ts'
import { listOrderOptions } from '@/modules/chatting/ui/chat-window/lists/consts'
import { isTruthyAndNotEmpty } from '@/utils/isTruthy'

import {
  taggableItemsFilterOptions,
  taggableItemsSortOptions,
} from '../constants'

export const useReleaseFormsData = () => {
  const creatorId = useUnit($creatorId)
  const [query, setQuery] = useState('')
  const [order, setOrder] = useState(listOrderOptions[0].value)
  const [sort, setSort] = useState(taggableItemsSortOptions[0].value)
  const [filter, setFilter] = useState(taggableItemsFilterOptions[0].value)

  const releaseFormsQuery = useInfiniteQuery(
    ['release-forms', creatorId, query, order, sort, order, filter],
    ({ pageParam = 0 }) =>
      creatorId
        ? OfReleaseFormsService.getReleaseForms({
            creatorId,
            offset: pageParam,
            sort,
            order,
            filter,
            query,
          })
        : undefined,
    {
      getNextPageParam: (page, pages) => {
        const allItemsCount = pages.flatMap((page) => page && page.items).length

        return page && page.hasMore ? allItemsCount : undefined
      },
      refetchOnMount: true,
      enabled: !!creatorId,
      cacheTime: 0,
    },
  )

  const releaseForms = useMemo(
    () =>
      releaseFormsQuery.data?.pages
        .flatMap((page) => page && page.items)
        .filter(isTruthyAndNotEmpty) ?? [],
    [releaseFormsQuery.data?.pages],
  )

  const handleSearchChange = debounce<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    ({ target: { value } }) =>
      (!value.length || value.length > 1) && setQuery(value),
    500,
  )

  const isLoading =
    releaseFormsQuery.isLoading && !releaseFormsQuery.isFetchingNextPage

  return {
    handleSearchChange,
    releaseForms,
    isLoading,
    canLoadMore: Boolean(
      releaseFormsQuery.hasNextPage && !releaseFormsQuery.isFetchingNextPage,
    ),
    onLoadMore: releaseFormsQuery.fetchNextPage,
    isLoadingMore: releaseFormsQuery.isFetchingNextPage,
    filterSortOrderOptions: {
      sort,
      order,
      setOrder,
      setSort,
      filter,
      setFilter,
      sortOptions: taggableItemsSortOptions,
      orderOptions: listOrderOptions,
      filterOptions: taggableItemsFilterOptions,
    },
  }
}
